import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  open: boolean;
  loading: boolean;
  originalLinkedinUrl: string;
  alteredLinkedinUrl: string;
  urlError: boolean;

  handleAlteredLinkedinUrlChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handlePasteAlteredLinkedinUrl(event: React.ClipboardEvent): void;

  handleClose(): void;

  handleAcceptRefinedLinkedinUrl(finalUrl: string): void;
}

const RefineLinkedinSearchDialog: React.FC<Props> = ({
  open,
  loading,
  originalLinkedinUrl,
  alteredLinkedinUrl,
  urlError,
  handleAlteredLinkedinUrlChange,
  handlePasteAlteredLinkedinUrl,
  handleClose,
  handleAcceptRefinedLinkedinUrl,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const handleSave = () => {
    const finalUrl = alteredLinkedinUrl.trim() === '' ? originalLinkedinUrl : alteredLinkedinUrl.trim();

    handleAcceptRefinedLinkedinUrl(finalUrl);
  };

  return (
    <Dialog open={open} classes={{ paper: classes.paper, container: classes.container }} fullWidth maxWidth={'md'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('reviewAndOptimizeSearch')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <div className={classes.innerContent}>
          <Typography className={classes.boldText} mb={'8px'}>
            {t('refineSearchStepsTitle')}
          </Typography>
          <Typography className={classes.greyText}>{t('refineSearchStep1')}</Typography>
          <Typography className={classes.greyText}>{t('refineSearchStep2')}</Typography>
          <Typography className={classes.greyText}>{t('refineSearchStep3')}</Typography>
          <Typography className={classes.greyText}>{t('refineSearchStep4')}</Typography>
        </div>
        <Stack className={classes.innerContent} gap={3} sx={(theme) => ({ borderTop: `1px solid ${theme.palette.greys.light}` })}>
          <div>
            <Typography className={classes.boldText} mb={'8px'}>
              {t('currentSearchUrl')}
            </Typography>
            <Typography className={classes.greyText} mb={'8px'}>
              {t('reviewCurrentSearch')}
            </Typography>
            <PaiLink
              text={_.truncate(originalLinkedinUrl, { length: 150 })}
              size={'lg'}
              variant={'primary'}
              underline={'hover'}
              href={originalLinkedinUrl}
              target={'_blank'}
            />
          </div>
          <div>
            <Typography className={classes.boldText} mb={'16px'}>
              {t('optimizedSearchUrl')}
            </Typography>
            <PaiTextField
              value={alteredLinkedinUrl}
              placeholder={t('enterAlteredLinkedinSearchUrlHere')}
              variant={'background'}
              selectOnFocus
              error={urlError}
              errorText={t('pleaseEnterValidLinkedinUrl')}
              onChange={handleAlteredLinkedinUrlChange}
              onPaste={handlePasteAlteredLinkedinUrl}
            />
          </div>
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton text={t('save')} border={'rounded'} size={'small'} action={handleSave} loading={loading} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  container: {
    maxHeight: '960px',
  },
  content: {
    padding: '0px !important',
    whiteSpace: 'pre-wrap',
  },
  innerContent: {
    padding: '20px 24px',
  },
  boldText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default RefineLinkedinSearchDialog;
